/**
 * The Russian vocabulary of the money layer.
 *
 * Every enum that reaches a trader's eyes is translated here and nowhere else.
 * The wire values in `packages/shared/src/ledger.ts` are never rendered: a
 * statement row saying `dispute_loss` is not an explanation, and a trader
 * reading one is a trader opening a ticket.
 *
 * The keying is exhaustive by construction — a `Record<LedgerKind, string>`
 * fails to compile the day a new kind is added to the shared contract, which is
 * exactly when someone needs to write its Russian.
 */

import {
  LedgerAccount,
  LedgerKind,
  type CardBlockReason,
  type TrafficBlockReason,
} from '@shared/ledger';

// ---------- Ledger ----------

/** Short enough for a statement row. Says what happened, not which table moved. */
export const LEDGER_KIND_LABEL: Record<LedgerKind, string> = {
  [LedgerKind.Deposit]: 'Пополнение депозита',
  [LedgerKind.Freeze]: 'Заморозка под платёж',
  [LedgerKind.Unfreeze]: 'Возврат в депозит',
  [LedgerKind.Release]: 'Выпуск мерчанту',
  [LedgerKind.Commission]: 'Ваша комиссия',
  [LedgerKind.DisputeLoss]: 'Списание по спору',
  [LedgerKind.Withdrawal]: 'Вывод средств',
  [LedgerKind.WithdrawalReversal]: 'Возврат вывода',
  [LedgerKind.Adjustment]: 'Корректировка',
};

/**
 * The second line of a statement row: why the figure moved.
 *
 * This is the whole reason the ledger exists on the trader's side. A trader
 * opens it when a number surprised them, so each line answers "where did my
 * money go" in the terms they think in — залог, платёж, комиссия — rather than
 * in accounts and signs.
 */
export const LEDGER_KIND_HINT: Record<LedgerKind, string> = {
  [LedgerKind.Deposit]: 'Вы пополнили залог. Он обеспечивает приём платежей.',
  [LedgerKind.Freeze]:
    'Под новый платёж зарезервирована часть залога. Она вернётся или уйдёт мерчанту, когда платёж закроется.',
  [LedgerKind.Unfreeze]: 'Платёж отменён или истёк — резерв вернулся в депозит.',
  [LedgerKind.Release]: 'Вы подтвердили получение рублей, и резерв ушёл мерчанту.',
  [LedgerKind.Commission]: 'Ваш заработок с платежа. Эти средства можно вывести.',
  [LedgerKind.DisputeLoss]:
    'Спор решён в пользу мерчанта: резерв ушёл ему, рубли остались у вас.',
  [LedgerKind.Withdrawal]: 'Средства отправлены на указанный вами адрес.',
  [LedgerKind.WithdrawalReversal]: 'Перевод не прошёл, сумма вернулась на доступный баланс.',
  [LedgerKind.Adjustment]: 'Ручная правка администратора. Причина указана в записи.',
};

export const LEDGER_ACCOUNT_LABEL: Record<LedgerAccount, string> = {
  [LedgerAccount.Deposit]: 'Депозит',
  [LedgerAccount.Frozen]: 'Заморожено',
  [LedgerAccount.Available]: 'Доступно к выводу',
};

/** Order in the filter, matching the order the accounts are explained on screen. */
export const LEDGER_ACCOUNT_ORDER: LedgerAccount[] = [
  LedgerAccount.Available,
  LedgerAccount.Deposit,
  LedgerAccount.Frozen,
];

/** Kinds a trader can filter by, in the order they meet them. */
export const LEDGER_KIND_ORDER: LedgerKind[] = [
  LedgerKind.Commission,
  LedgerKind.Freeze,
  LedgerKind.Unfreeze,
  LedgerKind.Release,
  LedgerKind.Deposit,
  LedgerKind.Withdrawal,
  LedgerKind.WithdrawalReversal,
  LedgerKind.DisputeLoss,
  LedgerKind.Adjustment,
];

// ---------- Why a card is not taking payments ----------

/** Keyed off the shared `CardBlockReason`, so the wording follows the rule. */
export const CARD_BLOCK_LABEL: Record<CardBlockReason, string> = {
  not_verified: 'Карта ещё не прошла проверку',
  disabled: 'Карта выключена вами',
  daily_limit: 'Достигнут лимит за сутки',
  concurrent_limit: 'Занята другим платежом',
  per_payment_limit: 'Платёж больше лимита на одну операцию',
};

/** One sentence saying what to do about it. */
export const CARD_BLOCK_HINT: Record<CardBlockReason, string> = {
  not_verified: 'Платежи пойдут на неё после проверки администратором.',
  disabled: 'Включите карту, чтобы на неё снова направляли платежи.',
  daily_limit: 'Лимит считается за скользящие 24 часа — карта освободится сама.',
  concurrent_limit:
    'Карта примет следующий платёж, когда закроется текущий. Поднимите лимит, чтобы принимать несколько сразу.',
  per_payment_limit: 'Такие суммы уйдут на другую карту. Поднимите лимит, чтобы принимать их здесь.',
};

// ---------- Why traffic is not coming ----------

export interface TrafficBlockCopy {
  /** Headline, in the trader's terms. */
  title: string;
  /** What it means for them right now. */
  body: string;
  /** Where the fix lives. `null` when the fix is the switch on this screen. */
  href: string | null;
  action: string;
}

/**
 * The server returns the first blocking reason, so exactly one of these is ever
 * shown. Each has its own destination: sending a trader whose collateral has
 * run out to the verification screen wastes the one minute in which a payment
 * could have been routed to them.
 */
export const TRAFFIC_BLOCK_COPY: Record<TrafficBlockReason, TrafficBlockCopy> = {
  kyc_not_approved: {
    title: 'Верификация не пройдена',
    body: 'Пока администратор не одобрит документы и карту, платежи вам не направляются.',
    href: '/verification',
    action: 'Перейти к верификации',
  },
  traffic_disabled: {
    title: 'Приём выключен',
    body: 'Новые платежи вам не направляются.',
    href: null,
    action: '',
  },
  no_usable_card: {
    title: 'Нет карты, готовой принять платёж',
    body: 'Нужна хотя бы одна проверенная и включённая карта, не упёршаяся в свой лимит.',
    href: '/cards',
    action: 'Открыть реквизиты',
  },
  insufficient_collateral: {
    title: 'Не хватает залога',
    // Deliberately does not say «пополните здесь»: the trader dashboard has no
    // top-up flow, and a button that leads to a screen without one is worse
    // than a sentence that explains the situation honestly.
    body: 'Под каждый платёж резервируется USDT из депозита, а свободного залога не хватает даже на один. Пока депозит не пополнен, платежи вам не направляются.',
    href: '/finance',
    action: 'Посмотреть баланс',
  },
};
